/**
 * One place that turns a plan check result into "on track / off track / undetermined" (#5456).
 *
 * The Home goal card and the Body Composition overview both call this, so the two can never show
 * different statuses for the same result. Kept free of Angular so a unit test can drive it
 * (`progress-status.test.ts`). See design-docs/coaching-dashboard.md, "The two status rules".
 */

import { ProgressStatus, fatTrendDirection } from '@cf-shared/body-comp-overview';
import { CheckupProgressSnapshot } from '@cf-shared/data-model/log-weekly-checkup';
import { BodyFatSeries } from '@cf-shared/plan-update-internals';
import { PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';
import { isProgressCheckUndetermined } from 'pages/weekly-checkup/weekly-checkup-outcome';

/** the parts of the plan check's result the status reads */
export interface ProgressStatusCheck {
  needsAdjustment: boolean;
  /** whether the check reached a verdict; only the `Checkup` rule reads it */
  actionDecided?: boolean;
  message?: PlanUpdateUserMessage;
}

/** which screen is asking; the screens differ in what they call "undetermined" */
export enum ProgressStatusRule {
  /** the Home goal card's rule: any message other than "all ok" without a needed change is undetermined */
  Home = 'Home',
  /**
   * the weekly check-up's rule: as `Home`, except that a deliberate "no change needed" which only
   * asks for measurements more often is a verdict (#6067). Using the wizard's own rule keeps the
   * badge from contradicting the steps the wizard shows beside it.
   */
  Checkup = 'Checkup',
}

/**
 * The status of one plan check result under the given rule.
 *
 * `Home`: on track when no change is needed and the message is AllOk; undetermined when no change
 * is needed and the message is anything else; off track when a change is needed.
 *
 * `Checkup`: off track when a change is needed; otherwise undetermined exactly where
 * `isProgressCheckUndetermined` says so, and on track everywhere else.
 */
export function progressStatus(check: ProgressStatusCheck, rule: ProgressStatusRule): ProgressStatus {
  if (check.needsAdjustment) {
    return ProgressStatus.OffTrack;
  }
  if (rule === ProgressStatusRule.Checkup) {
    const undetermined = isProgressCheckUndetermined({
      needsAdjustment: false,
      actionDecided: check.actionDecided === true,
      message: check.message,
    });
    return undetermined ? ProgressStatus.Undetermined : ProgressStatus.OnTrack;
  }
  return check.message === PlanUpdateUserMessage.AllOk ? ProgressStatus.OnTrack : ProgressStatus.Undetermined;
}

/** the parts of the plan check's result a stored snapshot copies */
export interface ProgressSnapshotSource extends ProgressStatusCheck {
  weightSlope_kgwk?: number;
  estimatedBW_kg?: number;
  fatnessSlopeBottom?: number;
  fatnessSlopeTop?: number;
  fatnessSeries?: BodyFatSeries;
  firstCountedDayIso?: string;
}

/**
 * What a weekly check-up stores about the progress check when it is completed: the status under
 * the check-up's own rule, the direction of the fat trend, and the figures behind both. The plan
 * check can only judge "now", so this is the only record of what it said at the time.
 *
 * Holds no calorie recommendation. Undefined when the wizard has no check result at all.
 */
export function buildCheckupProgressSnapshot(check: ProgressSnapshotSource | undefined): CheckupProgressSnapshot | undefined {
  if (!check) {
    return undefined;
  }
  const snapshot: CheckupProgressSnapshot = {
    status: progressStatus(check, ProgressStatusRule.Checkup),
    fatTrend: fatTrendDirection(check.fatnessSlopeBottom, check.fatnessSlopeTop),
    fatnessSeries: check.fatnessSeries,
    weightSlope_kgwk: check.weightSlope_kgwk,
    estimatedBW_kg: check.estimatedBW_kg,
    fatnessSlopeBottom: check.fatnessSlopeBottom,
    fatnessSlopeTop: check.fatnessSlopeTop,
    firstCountedDayIso: check.firstCountedDayIso,
  };
  // a key left undefined would be stored as nothing anyway; dropping it keeps the object exact
  for (const key of Object.keys(snapshot) as (keyof CheckupProgressSnapshot)[]) {
    if (snapshot[key] === undefined) delete snapshot[key];
  }
  return snapshot;
}
